import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ChevronsUpDown } from "lucide-react";
import { useAuthState } from "@/data/user";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@sphinx/design-system/dropdown-menu";
import { cn } from "@/lib/utils";
import { useHostList } from "@/hooks/useClients";
import { usePreviewedHostId } from "@/hooks/use-previewed-client-id";
import { buildHostsPath } from "@/lib/app-navigation";
import { resolveHostLogoByName } from "@/lib/host-logo";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { track } from "@/lib/analytics";

/**
 * Client selector for the Connect host canvas, mounted in the canvas nav row
 * beside the Servers|Client view selector (it used to float over the flow —
 * one nav row reads as chrome instead of covering canvas content).
 *
 * Replaces the header `HostOverlayBar` while the canvas is open (the header
 * instance hides itself — see `GlobalHostBar`). A single switcher pill that
 * opens the fixed target list. The menu opens downward, under its trigger.
 */

const ANALYTICS_LOCATION = "host_canvas";

// Sits on the nav row's own surface, so no elevation/blur — just a bordered
// pill sized to line up with the view selector and the Save button.
const PILL_CLASS =
  "flex items-center rounded-xl border border-border/60 bg-card/60 p-0.5";

/** Height of the interactive controls inside each pill. */
const CONTROL_HEIGHT = "h-8";

interface HostCanvasSelectorProps {
  projectId: string;
  activeHostId: string;
}

export function HostCanvasSelector({
  projectId,
  activeHostId,
}: HostCanvasSelectorProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthState();
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const { hosts, isLoading } = useHostList({ isAuthenticated, projectId });
  const [, setPreviewedHostId] = usePreviewedHostId(projectId);
  const [menuOpen, setMenuOpen] = useState(false);

  // Canonical target-matrix order (see data/targets.ts) — same as the header
  // pager, so the `n / total` position chip is stable across surfaces.
  const sortedHosts = hosts;

  const activeIndex = useMemo(
    () => sortedHosts.findIndex((h) => h.hostId === activeHostId),
    [sortedHosts, activeHostId]
  );
  const active = activeIndex >= 0 ? sortedHosts[activeIndex] : null;

  const switchTo = (hostId: string) => {
    if (hostId === activeHostId) return;
    track("connect_host_overlay_swapped", {
      location: ANALYTICS_LOCATION,
      from: activeHostId,
      to: hostId,
      host_count: hosts.length,
    });
    setPreviewedHostId(hostId);
    // The URL is the source of truth for which host the canvas renders
    // (see App's hosts-route sync) — replace so switching doesn't pile
    // history entries.
    navigate(buildHostsPath(hostId), { replace: true });
  };

  const logoFor = (name: string) => resolveHostLogoByName(name, themeMode);

  if (isLoading || !active) {
    return (
      <div className="flex items-center gap-1.5">
        <div className="h-9 w-32 animate-pulse rounded-xl border border-border/60 bg-card/80" />
        <div className="h-9 w-40 animate-pulse rounded-xl border border-border/60 bg-card/80" />
      </div>
    );
  }

  return (
    <div
      className="flex min-w-0 items-center gap-1.5"
      data-testid="host-canvas-selector"
    >
      {/* Switcher. Click to see all targets and switch between them. */}
      <div className={cn(PILL_CLASS, "min-w-0")}>
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Client used for preview"
              data-testid="host-canvas-current"
              className={cn(
                "flex min-w-0 items-center gap-1.5 rounded-lg pr-1.5 pl-2 outline-none transition-colors",
                CONTROL_HEIGHT,
                "hover:bg-muted/50 data-[state=open]:bg-muted/50",
                "focus-visible:ring-2 focus-visible:ring-ring/45"
              )}
            >
              <img
                src={logoFor(active.name)}
                alt=""
                className="size-4 shrink-0 object-contain"
              />
              <span className="max-w-[10rem] truncate text-sm font-semibold">
                {active.name}
              </span>
              <span className="shrink-0 rounded-full border border-border/60 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
                {activeIndex + 1} / {sortedHosts.length}
              </span>
              <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          {/* Opens downward, under the trigger — over the canvas below the nav row. */}
          <DropdownMenuContent
            side="bottom"
            align="start"
            sideOffset={10}
            className="min-w-[15rem]"
          >
            <DropdownMenuRadioGroup
              value={activeHostId}
              onValueChange={switchTo}
            >
              {sortedHosts.map((host) => (
                <DropdownMenuRadioItem
                  key={host.hostId}
                  value={host.hostId}
                  hideIndicator
                  className="group gap-2.5 py-2 pr-1.5"
                >
                  <img
                    src={logoFor(host.name)}
                    alt=""
                    className="size-4 shrink-0 object-contain"
                  />
                  <span
                    className="flex-1 truncate"
                    data-testid={`host-canvas-label-${host.hostId}`}
                  >
                    {host.name}
                  </span>
                  {host.hostId === activeHostId ? (
                    <span
                      aria-hidden
                      data-testid={`host-canvas-selected-dot-${host.hostId}`}
                      className="size-1.5 shrink-0 rounded-full bg-primary"
                    />
                  ) : null}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

    </div>
  );
}
